import React, { useState } from "react";
import "./App.css";
import { Affix, FloatButton, Layout, Menu, Tooltip, Flex, Button, ConfigProvider, theme, Typography } from "antd";
import Icon, {
  HomeOutlined,
  UserOutlined,
  VideoCameraOutlined,
  ContactsOutlined,
  SendOutlined,
  PlusOutlined,
  LineOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  MoonOutlined,
  SunOutlined,
  AppstoreOutlined
} from "@ant-design/icons";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import type { GetProps } from "antd";
import type { MenuProps } from "antd";
// 引入 framer-motion 做动画
import { motion, AnimatePresence, Variants } from "framer-motion";

import Emotion from "./Emotion";
import Wiki from "./Wiki";
import Manage from "./Manage";
import Home from "./Home";
import GetFile from "./GetFile";
import Marshmallow from "./Marshmallow";
import MarshmallowCreate from "./MarshmallowCreate";
import MarshmallowSuccess from "./MarshmallowSuccess";
import Login from "./Login";
import Dashboard from "./Dashboard";
import MarshmallowPreview from "./MarshmallowPreview";
import MarshmallowPublicPreview from "./MarshmallowPublicPreview";
import MarshmallowCategory from "./MarshmallowCategory";
import Video from "./Video";
import VideoRecord from "./VideoRecord";
import VideoCut from "./VideoCut";
import GetKg from "./GetKg";
import ReactLive2d from "react-live2d";
import { isMobile } from "react-device-detect";
import Tools from "./Tools";
import Song from "./Song";
import SongManage from "./SongManage";
import VoiceManage from "./VoiceManage";
import Voice from "./Voice";
import VoiceCategoryManage from "./VoiceCategoryManage";
import Activity from "./Activity";
import { AnimationContext } from "./AnimationContext";
import Update from "./Update";
import Privacy from "./Privacy";
import DownloadPage from "./AppDownload";
import RedirectPage from "./Landing";
import ArchivedList from "./ArchivedList";
import ArchivedMarshmallowPreview from "./ArchivedMarshmallowPreview";

type CustomIconComponentProps = GetProps<typeof Icon>;
const { Content, Footer, Sider } = Layout;
const {Text} = Typography;

const getPageVariants = (isFast: boolean): Variants => ({
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: isFast ? 550 : 150, 
      damping: isFast ? 55 : 15,
    },
  },
  out: {
    opacity: 0,
    y: -20,
    transition: {
      duration: isFast ? 0.1 : 0.2, // 离开动画也相应加快
      ease: "easeIn",
    },
  },
});

const AnimatedPage = ({ children }: { children: React.ReactNode }) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const location = useLocation();
  
  // 判断是否是 Dashboard 页面 (以 /dashboard 开头的路径)
  const isDashboard = location.pathname.startsWith('/dashboard');
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={getPageVariants(isDashboard)}
      style={{ width: "100%", height: "100%" }}
      onAnimationComplete={(definition) => {
        // definition 是当前动画状态的名称 (这里是 'in')
        if (definition === 'in') {
          console.log("页面进场动画结束，允许滚动");
          setIsAnimationComplete(true);
        }
      }}
    >
      <AnimationContext.Provider value={isAnimationComplete}>
      {children}
      </AnimationContext.Provider>
    </motion.div>
  );
};

const EmojiIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    p-id="7258"
  >
    <path
      d="M942.577061 507.011382c0-237.637282-192.632275-430.269557-430.269557-430.269557s-430.269557 192.632275-430.269557 430.269557c0 237.647515 192.632275 430.27979 430.269557 430.27979 97.553827 0 186.878225-33.189913 259.055081-87.88563 2.964518-3.363607 4.91187-7.655354 4.91187-12.479219 0-10.531867-8.541537-19.073404-19.072381-19.073404-5.465478 0-10.334369 2.168386-13.808494 5.842055l-0.242524 0c-64.584947 47.526433-144.086629 75.981509-230.422973 75.981509-215.179804 0-389.645324-174.489056-389.645324-389.656581 0-215.201294 174.466544-389.644301 389.645324-389.644301 215.202317 0 389.645324 174.443008 389.645324 389.644301 0 65.738213-15.423271 127.60219-44.186362 181.922353l0 0.321318c-0.533143 1.814322-1.108241 3.583618-1.108241 5.53097 0 10.531867 8.541537 19.073404 19.073404 19.073404 8.207939 0 15.06716-5.265934 17.745153-12.523221l0 0.13303C924.544359 645.279493 942.577061 578.258053 942.577061 507.011382zM399.574976 391.378805c0-24.028253-19.47147-43.499723-43.500746-43.499723-24.029276 0-43.478234 19.47147-43.478234 43.499723 0 24.029276 19.448958 43.479257 43.478234 43.479257C380.102483 434.858062 399.574976 415.408081 399.574976 391.378805zM669.735999 347.945597c-24.004717 0-43.476187 19.448958-43.476187 43.478234 0 24.029276 19.47147 43.500746 43.476187 43.500746 24.029276 0 43.50177-19.47147 43.50177-43.500746C713.237769 367.394554 693.766298 347.945597 669.735999 347.945597zM702.196295 630.742405c0-11.793604-9.558703-21.330818-21.350261-21.330818-1.88186 0-3.562129 0.620124-5.335519 1.084705l-0.441045 0c-49.473785 22.723537-104.548124 38.610366-162.562423 38.610366-57.130162 0-111.007233-16.130376-159.907966-38.233789l-0.177032 0c-2.300392-0.818645-4.690836-1.461281-7.2798-1.461281-11.880585 0-21.505803 9.602705-21.505803 21.485337 0 8.120958 4.513804 15.222703 11.129478 18.872836 54.209646 24.6494 113.883747 42.980907 177.319521 42.980907 63.037708 0 124.240629-18.686594 178.185238-43.046399 0.308015-0.149403 0.217964-0.11154 0.070608-0.045025C696.59574 645.928269 702.196295 638.545114 702.196295 630.742405z"
      p-id="7259"
    ></path>
  </svg>
);

const EmojiOutlined = (props: Partial<CustomIconComponentProps>) => (
  <Icon component={EmojiIcon} {...props} />
);

const items = [
  [HomeOutlined, "主页", "/"],
  [UserOutlined, "百科", "/wiki"],
  [UnorderedListOutlined, "玲奈歌单", "/song"],
  [VideoCameraOutlined, "录播&切片", "/video"],
  [EmojiOutlined, "表情包", "/emotion"],
  [ContactsOutlined, "小镇职工", "/staff"],
  [SendOutlined, "棉花糖", "/marshmallow"],
  [ToolOutlined, "奇怪的东西", "/tools"],
  [AppstoreOutlined, "狐哩狐哩APP", "/app/download"],
].map((icon: any, index) => ({
  key: String(index),
  icon: React.createElement(icon[0]),
  label: (
    <Link
      to={icon[2]}
      onMouseEnter={() => {
        // ... (保持原有的 Live2D 交互逻辑不变) ...
        const paths = ["/wiki", "/emotion", "/marshmallow"];
        const msgs = ["不许看黑历史", "有很多可爱的表情包喔", "是棉花糖！"];
        const idx = paths.indexOf(icon[2]);
        if (idx !== -1) {
            const printNow = document.getElementById("live2d-print");
            if (!isMobile && printNow) {
                printNow.innerHTML = msgs[idx];
                printNow.style.display = "block";
                setTimeout(() => {
                    printNow.style.display = "none";
                }, 3000);
            }
        }
      }}
    >
      {icon[1]}
    </Link>
  ),
}));

// 将原本 App 内部的逻辑提取到 AppContent 中
// 这样 AppContent 就可以在 Router 内部运行，从而使用 useLocation
const AppContent = () => {
  const onMenuChange: MenuProps["onClick"] = (event) => {
    console.log(event);
  };
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });
  const [showLive, setShowLive] = useState(true);
  
  // 使用 useLocation 替代 window.location
  const location = useLocation();
  // 提取路径逻辑 (兼容你原来的 path 数组逻辑)
  const path = location.pathname.split("/");
  
  // 判断是否是 Voice 独立页面
  const isVoicePage = (path.length > 2 && path[1] === "voice") || (location.pathname === "/app/download" ) || (path.length >= 2 && path[1].startsWith("landing"));
  console.log(isVoicePage, path)
  const pages = [
    "",
    "wiki",
    "song",
    "video",
    "emotion",
    "staff",
    "marshmallow",
  ];

  // 默认选中的 menu key
  const defaultSelectedKey = String(pages.indexOf(path[1] || ""));
  const [isDark, setIsDark] = useState(false);

  // 修改处：接收 event 参数以获取点击坐标
  const changeDark = (event: React.MouseEvent<HTMLElement>) => {
    // 获取切换逻辑的闭包函数
    const switchTheme = () => {
      if (isDark) {
        document.body.classList.remove("dark");
        setIsDark(false);
      } else {
        document.body.classList.add("dark");
        setIsDark(true);
      }
    };

    // 降级兼容：如果浏览器不支持 View Transitions API，直接切换
    if (!document.startViewTransition) {
      switchTheme();
      return;
    }

    // 获取点击坐标
    const x = event.clientX;
    const y = event.clientY;

    // 计算圆半径：从点击点到屏幕最远角落的距离
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    // 开始视图过渡
    // React 的状态更新是异步的，Antd 的 ConfigProvider 需要时间渲染
    // flushSync 不是必须的，但对于 ConfigProvider 这种重组件，直接包裹通常也行
    const transition = document.startViewTransition(async () => {
      switchTheme();
      // 等待 React 稍微反应一下（可选，通常直接同步执行也可以）
      await new Promise((resolve) => setTimeout(resolve, 0));
    });

    // 动画执行
    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`, // 初始状态：点击处的一个点
        `circle(${endRadius}px at ${x}px ${y}px)`, // 结束状态：覆盖全屏的大圆
      ];

      // 对新视图（即切换后的主题）应用动画
      document.documentElement.animate(
        {
          clipPath: isDark ? [...clipPath].reverse() : clipPath, // 变亮和变暗方向可以不同，或者保持一致
        },
        {
          duration: 700, // 动画时长 500ms
          easing: "ease-in-out", // 缓动函数
          // 指定伪元素进行动画
          pseudoElement: isDark
            ? "::view-transition-old(root)"
            : "::view-transition-new(root)",
        }
      );
    });
  };

  return (
    <ConfigProvider theme={{
        algorithm: isDark?  theme.darkAlgorithm : theme.defaultAlgorithm,
      }}>
    <>
      {isVoicePage ? (
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/voice/:url" element={<AnimatedPage><Voice /></AnimatedPage>} />
            <Route path="/voice/" element={<AnimatedPage><Voice /></AnimatedPage>} />
            <Route path="/app/download" element={<AnimatedPage><DownloadPage /></AnimatedPage>} />
            <Route path="/landing" element={<AnimatedPage><RedirectPage /></AnimatedPage>} />
          </Routes>
        </AnimatePresence>
      ) : (
        <Layout
          style={{
            minHeight: "100vh",
          }}
        >
          <Affix offsetTop={0}>
            <Sider
              onPointerMove={(e) => {
                setPosition({
                  x: Math.sqrt(e.clientX) / 10,
                  y: e.clientY / 1000 - Math.sqrt(e.clientX) / 20 - 5,
                });
              }}
              theme="light"
              breakpoint="lg"
              collapsedWidth="0"
              style={{
                position: "relative",
                minHeight: "100vh",
              }}
            >
              <div className="demo-logo-vertical" />
              <a href="https://space.bilibili.com/35231887/">
                <img
                  src="/logo1024.png"
                  alt="星海玲奈"
                  width={"50%"}
                  style={{
                    borderRadius: "100%",
                    margin: "10px 25%",
                  }}
                ></img>
              </a>
              <Text
                style={{
                  width: '100%',
                  display: 'block',
                  textAlign: "center",
                  margin: "0 0 10px 0",
                }}
              >
                是清楚可爱的北极狐
              </Text>
              <Flex align="center" justify="center" style={{margin: '0 0 10px 0'}}>
                 {
                  isDark ? <Button type="text" onClick={changeDark} icon={<SunOutlined />}>浅色模式</Button> : <Button onClick={changeDark} icon={<MoonOutlined />} type="text">深色模式</Button>
                 }
                
              </Flex>
              <Menu
                mode="inline"
                // 使用计算出来的 defaultSelectedKey
                defaultSelectedKeys={[defaultSelectedKey !== "-1" ? defaultSelectedKey : "0"]}
                items={items}
                onClick={onMenuChange}
                style={{
                  zIndex: 10,
                  position: "sticky",
                }}
              />
               {/* <div
                style={{
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    height: 500,
                }}>
                <img
                    src='/bigboard.png'
                    alt='星海玲奈'
                    width={'100%'}
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        zIndex: 3,
                        transform: `translate(${position.x}px, ${position.y}px)`,
                        overflow: 'hidden',
                        transition: '0.1s',
                    }}></img>
                <img
                    src='/flower.png'
                    alt='花花'
                    width={'100%'}
                    style={{
                        position: 'absolute',
                        bottom: 130,
                        zIndex: 2,
                    }}></img>
            </div> */}
            </Sider>
          </Affix>
          <Layout>
            <Content style={{ margin: "24px 16px 0" }}>
              {/* 核心动画区域：AnimatePresence 包裹 Routes */}
              {/* mode="wait" 确保上一个页面动画结束，新页面再进入 */}
              <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                  <Route path="/" element={<AnimatedPage><Home /></AnimatedPage>} />
                  <Route path="/wiki" element={<AnimatedPage><Wiki /></AnimatedPage>} />
                  <Route path="/video" element={<AnimatedPage><Video /></AnimatedPage>} />
                  <Route path="/video/replay" element={<AnimatedPage><VideoRecord /></AnimatedPage>} />
                  <Route path="/video/cut" element={<AnimatedPage><VideoCut /></AnimatedPage>} />
                  <Route path="/emotion" element={<AnimatedPage><Emotion /></AnimatedPage>} />
                  <Route path="/staff" element={<AnimatedPage>小镇居民</AnimatedPage>} />
                  <Route path="/marshmallow" element={<AnimatedPage><Marshmallow /></AnimatedPage>} />
                  <Route path="/activity" element={<AnimatedPage><Activity /></AnimatedPage>} />
                  <Route path="/marshmallow/create" element={<AnimatedPage><MarshmallowCreate /></AnimatedPage>} />
                  <Route path="/marshmallow/success" element={<AnimatedPage><MarshmallowSuccess /></AnimatedPage>} />
                  <Route path="/dashboard/manage" element={<AnimatedPage><Manage /></AnimatedPage>} />
                  <Route path="/file/:fileName" element={<AnimatedPage><GetFile /></AnimatedPage>} />
                  <Route path="/login" element={<AnimatedPage><Login /></AnimatedPage>} />
                  <Route path="/dashboard" element={<AnimatedPage><Dashboard /></AnimatedPage>} />
                  <Route path="/dashboard/category" element={<AnimatedPage><MarshmallowCategory /></AnimatedPage>} />
                  <Route path="/dashboard/marshmallow/:id" element={<AnimatedPage><MarshmallowPreview /></AnimatedPage>} />
                  <Route path="/marshmallow/:id" element={<AnimatedPage><MarshmallowPublicPreview /></AnimatedPage>} />
                  <Route path="/kg" element={<AnimatedPage><GetKg /></AnimatedPage>} />
                  <Route path="/tools" element={<AnimatedPage><Tools /></AnimatedPage>} />
                  <Route path="/song" element={<AnimatedPage><Song /></AnimatedPage>} />
                  <Route path="/dashboard/song" element={<AnimatedPage><SongManage /></AnimatedPage>} />
                  <Route path="/dashboard/update" element={<AnimatedPage><Update /></AnimatedPage>} />
                  <Route path="/dashboard/voice/:cid" element={<AnimatedPage><VoiceManage /></AnimatedPage>} />
                  <Route path="/dashboard/voiceCategory" element={<AnimatedPage><VoiceCategoryManage /></AnimatedPage>} />
                  <Route path="/voice/:url" element={<AnimatedPage><Voice /></AnimatedPage>} />
                  <Route path="/voice/" element={<AnimatedPage><Voice /></AnimatedPage>} />
                  <Route path="/privacy/" element={<AnimatedPage><Privacy /></AnimatedPage>} />
                  <Route path="/dashboard/archived" element={<AnimatedPage><ArchivedList /></AnimatedPage>} />
                  <Route path="/dashboard/archived/:id" element={<AnimatedPage><ArchivedMarshmallowPreview /></AnimatedPage>} />
                </Routes>
              </AnimatePresence>
            </Content>
            {!isMobile && (
              <ReactLive2d
                color="#A388D0"
                width={240}
                height={260}
                right={"20px"}
                ModelList={["l2d"]}
                TouchDefault={[
                  "坏了，有bt",
                  "笨蛋不要乱碰啦",
                  "小狐凃最可爱了",
                  "这里是可爱狐狐~",
                  "关注星海玲奈谢谢喵",
                  "再碰我就给你一拳！",
                  "不许看黑历史",
                  "故事视频很好看的喔",
                  "大笨蛋！全都是大笨蛋！",
                  "可爱！吃了！",
                  "可以喂我吃鱼吗",
                  "快把你的鼠标移开！",
                ]}
                TouchHead={[
                  "嘿咻，嘿咻",
                  "笨蛋不许捏我脸",
                  "摸摸头",
                  "再捏就给你一拳！",
                  "大坏蛋！",
                ]}
                TouchBody={[
                  "小小的纸箱子，三轮很喜欢",
                  "可回收~",
                  "诶诶诶别把我箱子抢走啊",
                  "狐狐是最可爱的",
                  "打包带走",
                ]}
                PathFull="https://dd.lynne.com.cn/Resources/"
              />
            )}
            <FloatButton.Group shape="circle" style={{ insetInlineEnd: 24 }}>
              {!isMobile && (
                <Tooltip title={showLive ? "隐藏狐狐" : "显示狐狐"}>
                  <FloatButton
                    icon={showLive ? <LineOutlined /> : <PlusOutlined />}
                    type="primary"
                    style={{ insetInlineEnd: 74 }}
                    onClick={() => {
                      const element = document.getElementById("live2d-container");
                      if (showLive) {
                        if (element) {
                          element.style.display = "none";
                        }
                        setShowLive(false);
                      } else {
                        if (element) {
                          element.style.display = "block";
                        }
                        setShowLive(true);
                      }
                    }}
                  />
                </Tooltip>
              )}
              <FloatButton.BackTop />
            </FloatButton.Group>

            <Footer style={{ textAlign: "center" }}>
              狐哩狐哩 - 玲奈的dd站❤爱来自小狐凃 ©{new Date().getFullYear()}
              <a href="https://beian.miit.gov.cn/">京ICP备2024099964号-2</a>
            </Footer>
          </Layout>
        </Layout>
      )}
    </>
    
      </ConfigProvider>
  );
};

// 主 App 组件只负责提供 Router 上下文
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;